import { AxiosResponse } from 'axios';
import { ResponseCodes } from 'src/enums/responseCodes';
import { throttle } from '../config';
import { PaginationAndFilter } from '../interfaces/common/pagination';
import type { ProductModel, ProductFormModel } from '../interfaces/masters/base/product';
import { applyFilters } from '../utils/apply-filters';
import { applyPagination } from '../utils/apply-pagination';
import { applySort } from '../utils/apply-sort';
import { wait } from '../utils/wait';
import axiosClient from '../utils/axios-client';

interface Options {
  filters?: Array<{ operator: string; property: string; value: any }>;
  sort?: string;
  sortBy?: string;
  page?: number;
  query?: string;
  view?: string;
}

class ProductApi {
  async getProduct(options: Options):
  Promise<{ products: ProductModel[], productsCount: number }> {
    if (throttle) {
      await wait(throttle);
    }

    const products : ProductModel[] = await axiosClient.get('master/product',).then((response) => (
      response.data
    ));

    const { filters, sort, sortBy, page, query, view } = options;

    /*
     NOTE: Query, filter, sort and pagination are operation meant to be executed on the server.
     Since this does not connect to a real backend, we simulate these operations.
     */

    const queriedProducts = products.filter((_product) => {
      // If query exists, it looks only in customer full name field
      if (!!query && !(
        _product.name?.toLowerCase().includes(query.toLowerCase())
        || _product.id?.toLowerCase().includes(query.toLowerCase()))) {
        return false;
      }

      // No need to look for any resource fields
      if (typeof view === 'undefined' || view === 'all') {
        return true;
      }

      return true;
    });
    const filteredProducts = applyFilters(queriedProducts, filters);
    const sortedProducts = applySort(filteredProducts, sort, sortBy);
    const paginatedProducts = applyPagination(sortedProducts, page);

    return Promise.resolve({
      products: paginatedProducts,
      productsCount: filteredProducts.length
    });
  }

  // calls api to get all product without filters
  async getAllProducts():Promise<ProductModel[]> {
    if (throttle) {
      await wait(throttle);
    }

    const products : ProductModel[] = await axiosClient.get('master/product').then((response) => (
      response.data
    ));

    return Promise.resolve(products);
  }

  // calls api to get the product by id
  async getProductById(productId: string): Promise<ProductModel> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get(`master/product/getById/${productId}`);

    if (response.status === ResponseCodes.NOT_FOUND) {
      response.data = null;
    }
    return response.data;
  }

  // calls api to add product in the database
  async addProduct(product: ProductFormModel): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.post('master/product', product);

    return response;
  }

  // calls put api to update product
  async updateProduct(product: ProductFormModel):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const response = await axiosClient.put('master/product', product);

      return await Promise.resolve({
        responseData: response.data,
        statusCode: response.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls api to delete product in the database
  async deleteProduct(productId: string): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.delete(`master/product/${productId}`);

    return response;
  }

  // get all products based on option filter
  async getProductsByOptions(options: any):
  Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get('master/product/getByOptions', { params: options });

    return response;
  }

  async getProductsByPaginationAndFilters(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post('master/product/getByPaginationAndFilter', options).then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getProductFilterData():Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get('master/product/filtersData');
    return response;
  }
}

export const productApi = new ProductApi();
